import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useCheckUserRole } from '../hooks/useCheckUserRole';
import './Admin.css';

const TABS = [
    { key: 'users', label: 'Kullanıcılar', icon: 'fa-users' },
    { key: 'blogs', label: 'Bloglar', icon: 'fa-newspaper' },
    { key: 'broken', label: 'Sorunlu Üyelikler', icon: 'fa-exclamation-triangle' },
];

const SECTION_INFO = {
    users: {
        title: 'Kullanıcı Yönetimi',
        description: 'Yakında bitecek aktif üyelikler önce gösterilir. Kullanıcıları ara, rollerini ve üyelik sürelerini tek ekrandan yönet.',
        createLabel: 'Yeni kullanıcı',
    },
    blogs: {
        title: 'Blog Yönetimi',
        description: 'Editör içeriklerini görüntüle, oluştur ve güncelle.',
        createLabel: 'Yeni blog',
    },
    broken: {
        title: 'Sorunlu Üyelikler',
        description: 'Süresi dolduğu hâlde rolü açık kalmış kullanıcıları düzelt.',
    },
};

const TABLE_COLUMNS = {
    blogs: [
        { key: 'id', label: 'ID' },
        { key: 'blogname', label: 'Başlık' },
        { key: 'author', label: 'Yazar' },
        { key: 'date', label: 'Tarih', kind: 'date' },
        { key: 'image_filename', label: 'Görsel' },
    ],
};

const FORM_FIELDS = {
    users: [
        { name: 'username', label: 'Kullanıcı adı' },
        { name: 'email', label: 'E-posta', type: 'email' },
        { name: 'tg_link', label: 'Telegram bağlantısı', required: false },
        {
            name: 'role', label: 'Rol', type: 'select', options: [
                { value: 'wait', label: 'Beklemede' },
                { value: 'user', label: 'Üye' },
                { value: 'editor', label: 'Editör' },
                { value: 'admin', label: 'Admin' },
            ],
        },
        { name: 'password', label: 'Şifre', type: 'password', editOptional: true },
    ],
    blogs: [
        { name: 'blogname', label: 'Başlık' },
        { name: 'author', label: 'Yazar' },
        { name: 'image_filename', label: 'Görsel dosya adı', required: false },
        { name: 'feedback', label: 'İçerik', type: 'textarea' },
    ],
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('tr-TR', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'Europe/Istanbul',
    }).format(date);
};

const subscriptionLabel = (status) => ({
    active: 'Aktif',
    expired: 'Süresi dolmuş',
    none: 'Kayıt yok',
}[status] || 'Bilinmiyor');

const AdminPanel = () => {
    useCheckUserRole(['admin']);

    const [currentTable, setCurrentTable] = useState('users');
    const [users, setUsers] = useState([]);
    const [records, setRecords] = useState({ blogs: [] });
    const [brokenSubs, setBrokenSubs] = useState([]);
    const [userMeta, setUserMeta] = useState({ total: 0, pages: 0, current_page: 1 });
    const [userPage, setUserPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [reloadToken, setReloadToken] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);
    const [formType, setFormType] = useState('add');
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setUserPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        const controller = new AbortController();

        const loadCurrentSection = async () => {
            setLoading(true);
            setError('');
            try {
                if (currentTable === 'users') {
                    const response = await axios.get('/api/users', {
                        params: { page: userPage, per_page: 50, search },
                        signal: controller.signal,
                    });
                    setUsers(response.data.items || []);
                    setUserMeta({
                        total: response.data.total || 0,
                        pages: response.data.pages || 0,
                        current_page: response.data.current_page || userPage,
                    });
                } else if (currentTable === 'broken') {
                    const response = await axios.get('/api/admin/broken-subscriptions', {
                        signal: controller.signal,
                    });
                    setBrokenSubs(response.data.users || []);
                } else {
                    const params = currentTable === 'blogs' ? { page: 1, per_page: 100 } : undefined;
                    const response = await axios.get(`/api/${currentTable}`, {
                        params,
                        signal: controller.signal,
                    });
                    const payload = response.data;
                    const items = Array.isArray(payload)
                        ? payload
                        : currentTable === 'blogs'
                            ? payload.blogs || []
                            : [];
                    setRecords((current) => ({ ...current, [currentTable]: items }));
                }
            } catch (requestError) {
                if (requestError.code !== 'ERR_CANCELED') {
                    setError(requestError.response?.data?.error || 'Veriler yüklenemedi.');
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };

        loadCurrentSection();
        return () => controller.abort();
    }, [currentTable, userPage, search, reloadToken]);

    const currentInfo = SECTION_INFO[currentTable];
    const currentRecords = records[currentTable] || [];
    const activeMembersOnPage = useMemo(
        () => users.filter((user) => user.subscription_status === 'active').length,
        [users]
    );

    const reloadCurrentSection = () => setReloadToken((value) => value + 1);

    const changeSection = (key) => {
        setCurrentTable(key);
        setSelectedItem(null);
        setFormType('add');
        setShowForm(false);
        setError('');
        setNotice('');
    };

    const openCreateForm = () => {
        setSelectedItem(null);
        setFormType('add');
        setShowForm(true);
        setNotice('');
    };

    const openEditForm = (item) => {
        setSelectedItem(item);
        setFormType('edit');
        setShowForm(true);
        setNotice('');
    };

    const closeForm = () => {
        setSelectedItem(null);
        setFormType('add');
        setShowForm(false);
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
        setActionLoading(true);
        setError('');
        try {
            await axios.delete(`/api/${type}/${id}`);
            setNotice('Kayıt silindi.');
            reloadCurrentSection();
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Kayıt silinemedi.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        const data = Object.fromEntries(new FormData(event.currentTarget).entries());
        setActionLoading(true);
        setError('');
        try {
            if (formType === 'add') {
                await axios.post(`/api/${currentTable}`, data);
            } else {
                await axios.put(`/api/${currentTable}/${selectedItem.id}`, data);
            }
            setNotice(formType === 'add' ? 'Yeni kayıt oluşturuldu.' : 'Kayıt güncellendi.');
            closeForm();
            reloadCurrentSection();
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'İşlem tamamlanamadı.');
        } finally {
            setActionLoading(false);
        }
    };

    const handlePurchaseSubscription = async (userId, durationType, durationDays = null) => {
        setActionLoading(true);
        setError('');
        try {
            const payload = { user_id: userId };
            if (durationDays !== null) {
                payload.duration_days = durationDays;
            } else {
                payload.duration_type = durationType;
            }
            const response = await axios.post('/api/purchase_subscription', {
                ...payload,
            });
            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Üyelik güncellenemedi.');
            }
            setNotice('Üyelik süresi güncellendi.');
            reloadCurrentSection();
        } catch (requestError) {
            setError(requestError.response?.data?.error || requestError.message || 'Üyelik güncellenemedi.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCustomDays = (userId) => {
        const value = window.prompt('Üyeliğe kaç gün eklensin?');
        if (value === null) return;

        const days = Number(value);
        if (!Number.isInteger(days) || days <= 0) {
            setError('Lütfen sıfırdan büyük, tam bir gün sayısı girin.');
            return;
        }

        handlePurchaseSubscription(userId, null, days);
    };

    const handleShortenSubscription = async (userId, mode) => {
        const body = { user_id: userId };
        if (mode === 'end_now') {
            if (!window.confirm('Üyelik hemen sonlandırılacak. Emin misiniz?')) return;
            body.shorten_days = 9999;
        } else if (mode === 'shorten_days') {
            const days = window.prompt('Üyelik kaç gün kısaltılsın?');
            if (!days || Number.isNaN(Number(days))) return;
            body.shorten_days = Number.parseInt(days, 10);
        } else return;

        setActionLoading(true);
        setError('');
        try {
            await axios.post('/api/shorten_subscription', body);
            setNotice('Üyelik bitiş tarihi güncellendi.');
            reloadCurrentSection();
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Üyelik tarihi güncellenemedi.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleBrokenSubscription = async (userId, action, durationMinutes) => {
        setActionLoading(true);
        setError('');
        try {
            const body = { action };
            if (durationMinutes) body.duration_minutes = durationMinutes;
            await axios.post(`/api/admin/fix-subscription/${userId}`, body);
            setNotice('Sorunlu üyelik düzeltildi.');
            reloadCurrentSection();
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Üyelik düzeltilemedi.');
        } finally {
            setActionLoading(false);
        }
    };

    const renderUserActions = (user) => (
        <div className="admin-user-actions">
            <div className="admin-action-row">
                <button type="button" className="admin-btn admin-btn-neutral" onClick={() => openEditForm(user)}>Düzenle</button>
                <button type="button" className="admin-btn admin-btn-danger-outline" onClick={() => handleDelete('users', user.id)}>Sil</button>
            </div>
            <div className="admin-action-row admin-subscription-actions">
                <button type="button" className="admin-btn admin-btn-soft" onClick={() => handlePurchaseSubscription(user.id, '1_day')}>3 Gün</button>
                <button type="button" className="admin-btn admin-btn-soft" onClick={() => handlePurchaseSubscription(user.id, '1_month')}>1 Ay</button>
                <button type="button" className="admin-btn admin-btn-soft" onClick={() => handlePurchaseSubscription(user.id, '3_month')}>3 Ay</button>
                <button type="button" className="admin-btn admin-btn-soft" onClick={() => handlePurchaseSubscription(user.id, '6_month')}>6 Ay</button>
            </div>
            <div className="admin-action-row">
                <button type="button" className="admin-btn admin-btn-warning" onClick={() => handleCustomDays(user.id)}>Gün ver</button>
                <button type="button" className="admin-btn admin-btn-neutral" onClick={() => handleShortenSubscription(user.id, 'shorten_days')}>Gün azalt</button>
                <button type="button" className="admin-btn admin-btn-danger" onClick={() => handleShortenSubscription(user.id, 'end_now')}>Bitir</button>
            </div>
        </div>
    );

    const renderUsers = () => (
        <>
            <div className="admin-user-summary">
                <div><span>Arama sonucu</span><strong>{userMeta.total}</strong></div>
                <div><span>Bu sayfadaki aktif üyeler</span><strong>{activeMembersOnPage}</strong></div>
                <div><span>Sayfa</span><strong>{userMeta.current_page || 1} / {userMeta.pages || 1}</strong></div>
            </div>
            <div className="admin-table-scroll">
                <table className="admin-table admin-users-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Kullanıcı</th>
                            <th>İletişim</th>
                            <th>Rol</th>
                            <th>Üyelik bitişi</th>
                            <th>Durum</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id}>
                                <td className="admin-id-cell">#{user.id}</td>
                                <td><strong>{user.username}</strong></td>
                                <td>
                                    <div>{user.email}</div>
                                    {user.tg_link && <small>{user.tg_link}</small>}
                                </td>
                                <td><span className={`admin-role admin-role-${user.role}`}>{user.role}</span></td>
                                <td>{formatDate(user.subscription_end_date)}</td>
                                <td>
                                    <span className={`admin-status admin-status-${user.subscription_status}`}>
                                        {subscriptionLabel(user.subscription_status)}
                                    </span>
                                </td>
                                <td>{renderUserActions(user)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!loading && users.length === 0 && <div className="admin-empty">Aramana uygun kullanıcı bulunamadı.</div>}
            </div>
            <div className="admin-pagination">
                <button type="button" disabled={userPage <= 1} onClick={() => setUserPage((page) => page - 1)}>Önceki</button>
                <span>{userMeta.current_page || 1}. sayfa</span>
                <button type="button" disabled={userPage >= (userMeta.pages || 1)} onClick={() => setUserPage((page) => page + 1)}>Sonraki</button>
            </div>
        </>
    );

    const renderGenericTable = () => {
        const columns = TABLE_COLUMNS[currentTable] || [];
        return (
            <div className="admin-table-scroll">
                <table className="admin-table">
                    <thead>
                        <tr>
                            {columns.map((column) => <th key={column.key}>{column.label}</th>)}
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRecords.map((item) => (
                            <tr key={item.id}>
                                {columns.map((column) => (
                                    <td key={column.key}>
                                        {column.kind === 'date' ? formatDate(item[column.key]) : (item[column.key] || '—')}
                                    </td>
                                ))}
                                <td>
                                    <div className="admin-action-row">
                                        <button type="button" className="admin-btn admin-btn-neutral" onClick={() => openEditForm(item)}>Düzenle</button>
                                        <button type="button" className="admin-btn admin-btn-danger-outline" onClick={() => handleDelete(currentTable, item.id)}>Sil</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!loading && currentRecords.length === 0 && <div className="admin-empty">Bu bölümde henüz kayıt yok.</div>}
            </div>
        );
    };

    const renderBrokenSubscriptions = () => (
        <div className="admin-table-scroll">
            <table className="admin-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Kullanıcı</th>
                        <th>E-posta</th>
                        <th>Son üyelik bitişi</th>
                        <th>Sorun</th>
                        <th>İşlem</th>
                    </tr>
                </thead>
                <tbody>
                    {brokenSubs.map((user) => (
                        <tr key={user.id}>
                            <td>#{user.id}</td>
                            <td><strong>{user.username}</strong></td>
                            <td>{user.email}</td>
                            <td>{formatDate(user.latest_end_date)}</td>
                            <td>{user.issue}</td>
                            <td>
                                <div className="admin-action-row">
                                    <button type="button" className="admin-btn admin-btn-danger" onClick={() => handleBrokenSubscription(user.id, 'revert_to_wait')}>Wait yap</button>
                                    <button type="button" className="admin-btn admin-btn-soft" onClick={() => handleBrokenSubscription(user.id, 'extend_subscription', 30 * 24 * 60)}>+1 Ay</button>
                                    <button type="button" className="admin-btn admin-btn-soft" onClick={() => handleBrokenSubscription(user.id, 'extend_subscription', 3 * 30 * 24 * 60)}>+3 Ay</button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {!loading && brokenSubs.length === 0 && <div className="admin-empty admin-empty-success">Sorunlu üyelik bulunamadı.</div>}
        </div>
    );

    const renderForm = () => {
        const fields = FORM_FIELDS[currentTable] || [];
        return (
            <section className="admin-form-card">
                <div className="admin-form-heading">
                    <div>
                        <span>{formType === 'add' ? 'Yeni kayıt' : `#${selectedItem?.id} düzenleniyor`}</span>
                        <h3>{formType === 'add' ? currentInfo.createLabel : 'Kaydı güncelle'}</h3>
                    </div>
                    <button type="button" className="admin-form-close" onClick={closeForm} aria-label="Formu kapat">×</button>
                </div>
                <form
                    className="admin-form-grid"
                    key={`${currentTable}-${formType}-${selectedItem?.id || 'new'}`}
                    onSubmit={handleFormSubmit}
                >
                    {fields.map((field) => {
                        const required = field.required !== false && !(field.editOptional && formType === 'edit');
                        const defaultValue = field.name === 'password' ? '' : (selectedItem?.[field.name] || '');
                        return (
                            <label className={field.type === 'textarea' ? 'admin-form-wide' : ''} key={field.name}>
                                <span>{field.label}{required && ' *'}</span>
                                {field.type === 'textarea' ? (
                                    <textarea name={field.name} defaultValue={defaultValue} required={required} rows="4" />
                                ) : field.type === 'select' ? (
                                    <select name={field.name} defaultValue={defaultValue || 'wait'} required={required}>
                                        {field.options.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}
                                    </select>
                                ) : (
                                    <input name={field.name} type={field.type || 'text'} defaultValue={defaultValue} required={required} />
                                )}
                                {field.editOptional && formType === 'edit' && <small>Boş bırakırsan mevcut şifre değişmez.</small>}
                            </label>
                        );
                    })}
                    <div className="admin-form-actions admin-form-wide">
                        <button type="button" className="admin-btn admin-btn-neutral" onClick={closeForm}>Vazgeç</button>
                        <button type="submit" className="admin-btn admin-btn-primary" disabled={actionLoading}>
                            {actionLoading ? 'Kaydediliyor…' : formType === 'add' ? 'Kaydı oluştur' : 'Değişiklikleri kaydet'}
                        </button>
                    </div>
                </form>
            </section>
        );
    };

    return (
        <div className="admin-page">
            <div className="breadcrumb-section breadcrumb-bg admin-breadcrumb">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 offset-lg-2 text-center">
                            <div className="breadcrumb-text">
                                <p>Yönetim merkezi</p>
                                <h1>Admin Paneli</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="admin-container">
                <nav className="admin-tabs" aria-label="Admin bölümleri">
                    {TABS.map((tab) => (
                        <button
                            type="button"
                            key={tab.key}
                            className={currentTable === tab.key ? 'active' : ''}
                            onClick={() => changeSection(tab.key)}
                        >
                            <i className={`fas ${tab.icon}`} aria-hidden="true" />
                            {tab.label}
                        </button>
                    ))}
                </nav>

                <section className="admin-content-card">
                    <header className="admin-content-header">
                        <div>
                            <span className="admin-eyebrow">Admin Paneli</span>
                            <h2>{currentInfo.title}</h2>
                            <p>{currentInfo.description}</p>
                        </div>
                        <div className="admin-header-actions">
                            {currentTable === 'users' && (
                                <label className="admin-search">
                                    <i className="fas fa-search" aria-hidden="true" />
                                    <input
                                        type="search"
                                        value={searchInput}
                                        onChange={(event) => setSearchInput(event.target.value)}
                                        placeholder="Kullanıcı adı veya e-postanın bir kısmını yaz"
                                    />
                                </label>
                            )}
                            {currentTable !== 'broken' && (
                                <button type="button" className="admin-btn admin-btn-primary" onClick={openCreateForm}>
                                    <i className="fas fa-plus" aria-hidden="true" /> {currentInfo.createLabel}
                                </button>
                            )}
                            <button type="button" className="admin-btn admin-btn-icon" onClick={reloadCurrentSection} aria-label="Yenile">
                                <i className="fas fa-sync" aria-hidden="true" />
                            </button>
                        </div>
                    </header>

                    {error && <div className="admin-alert admin-alert-error">{error}</div>}
                    {notice && <div className="admin-alert admin-alert-success">{notice}</div>}
                    {loading ? (
                        <div className="admin-loading"><span /> Veriler yükleniyor…</div>
                    ) : currentTable === 'users' ? (
                        renderUsers()
                    ) : currentTable === 'broken' ? (
                        renderBrokenSubscriptions()
                    ) : (
                        renderGenericTable()
                    )}
                </section>

                {showForm && currentTable !== 'broken' && renderForm()}
            </main>
        </div>
    );
};

export default AdminPanel;
